/* eslint-disable complexity */
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import {
  propTypesActiveDocMeta,
  propTypeslocalization,
} from '../../constants/Proptypes';
import { propTypesFooter } from './Footer/Footer.proptypes';
import { propTypesNavigation } from './Header/Header.proptypes';

import NewsletterSection from '../NewsletterSection';
import CookieBanner from './CookieBanner';
import DownloadSection from './DownloadSection';
import Footer from './Footer';
import Header from './Header';
import PromoBanner from './PromoBanner';

import { useTrackingCookies } from '../../utils/trackingParams';

import '../../../../assets/styles/common.css';
import '../../../../assets/styles/reset.css';
import { DocumentType } from '../../constants/Enums';
import * as S from './Layout.styles';

export const Layout = ({
  children,
  customNewsletter,
  footer,
  navigation,
  activeDocMeta,
  localization,
  downloadSectionData,
  hasNewsletter,
}) => {
  if (!children || !activeDocMeta) {
    return null;
  }

  useTrackingCookies();

  useEffect(() => {
    (() => {
      let lastCookie = document.cookie;
      // rename document.cookie to document._cookie, and redefine document.cookie
      const expando = '_cookie';
      let nativeCookieDesc = Object.getOwnPropertyDescriptor(
        Document.prototype,
        'cookie'
      );
      Object.defineProperty(Document.prototype, expando, nativeCookieDesc);
      Object.defineProperty(Document.prototype, 'cookie', {
        enumerable: true,
        configurable: true,
        get() {
          return this[expando];
        },
        set(value) {
          this[expando] = value;
          // check cookie change
          let cookie = this[expando];
          if (cookie !== lastCookie) {
            try {
              // dispatch cookie-change messages to other same-origin tabs/frames
              let detail = { oldValue: lastCookie, newValue: cookie };
              this.dispatchEvent(
                new CustomEvent('cookiechange', {
                  detail: detail,
                })
              );
              channel.postMessage(detail);
            } finally {
              lastCookie = cookie;
            }
          }
        },
      });
      // listen cookie-change messages from other same-origin tabs/frames
      const channel = new BroadcastChannel('cookie-channel');
      channel.onmessage = e => {
        lastCookie = e.data.newValue;
        document.dispatchEvent(
          new CustomEvent('cookiechange', {
            detail: e.data,
          })
        );
      };
    })();
  }, []);

  const [showCookieBanner, setShowCookieBanner] = useState(false);

  const { type, uid } = activeDocMeta || {};

  const {
    title = '',
    description = '',
    buttonLabel = '',
    resourceUrl = '',
    isFormRequired = false,
    dataTrackAttribute = '',
  } = downloadSectionData || {};

  const {
    data: {
      is_banner_activated: isBannerDisplayed,
      banner_exceptions: exceptions,
    } = {},
  } = localization || '';

  const isException =
    !!exceptions?.find(element => element.exception?.uid === uid) ||
    type === DocumentType.NOTFOUND;

  const hasHeader = navigation && localization;
  const hasDownloadSection = type === DocumentType.RESOURCE;
  const hasNewsletterSection = !hasNewsletter
    ? (type === DocumentType.HOMEPAGE ||
        type === DocumentType.NEWSLETTER ||
        type === DocumentType.POST ||
        type === DocumentType.CATEGORY ||
        type === DocumentType.TAG ||
        type === DocumentType.RESOURCE) &&
      !downloadSectionData
    : hasNewsletter;

  const isSectionFullScreen = type === DocumentType.NEWSLETTER;

  return (
    <>
      {hasHeader && (
        <Header
          activeDocMeta={activeDocMeta}
          localization={localization}
          navigation={navigation}
        />
      )}
      {isBannerDisplayed && !isException && (
        <PromoBanner localization={localization} />
      )}
      {children}
      {hasNewsletterSection && (
        <NewsletterSection
          customNewsletter={customNewsletter}
          isFullScreen={isSectionFullScreen}
          localization={localization}
        />
      )}
      <CookieBanner
        hideCookieBanner={() => setShowCookieBanner(false)}
        localization={localization}
        showCookieBanner={showCookieBanner}
      />
      {hasDownloadSection && <S.HorizontalLine />}
      {hasDownloadSection && (
        <DownloadSection
          buttonLabel={buttonLabel}
          dataTrackAttribute={dataTrackAttribute}
          description={description}
          isFormRequired={isFormRequired}
          resourceUrl={resourceUrl}
          title={title}
        />
      )}
      {footer && (
        <Footer
          activeDocMeta={activeDocMeta}
          footer={footer}
          openCookieBanner={() => setShowCookieBanner(true)}
        />
      )}
    </>
  );
};

Layout.propTypes = {
  /**
   * Language data from the current Prismic document.
   */
  activeDocMeta: PropTypes.shape(propTypesActiveDocMeta),
  /**
   * Content of the page.
   */
  children: PropTypes.node.isRequired,
  /**
   * Custom object allowing to update the newsletter page copy
   * depending of the Sendinblue list used in the query params.
   */
  customNewsletter: PropTypes.shape({
    custom_description: PropTypes.string,
    custom_title: PropTypes.string,
    sendinblue_list_id: PropTypes.string,
    is_wooflash: PropTypes.bool,
  }),
  /**
   * Display a download section instead of the newsletter section.
   */
  downloadSectionData: PropTypes.shape({
    buttonLabel: PropTypes.string,
    description: PropTypes.string,
    isFormRequired: PropTypes.bool,
    resourceUrl: PropTypes.string,
    title: PropTypes.string,
  }),
  /**
   * Footer document.
   */
  footer: PropTypes.shape(propTypesFooter),
  /**
   * Displays the newsletter block if true.
   */
  hasNewsletter: PropTypes.bool,
  /**
   * Localization document.
   */
  localization: PropTypes.shape(propTypeslocalization),
  /**
   * Navigation document.
   */
  navigation: PropTypes.shape(propTypesNavigation),
};
